export const POSTS_BATCH_6 = [
  {
    slug: "business-website-cost-india",
    title: "How Much Does a Business Website Cost in India?",
    excerpt: "₹5,000 freelancers to ₹5-lakh agencies — what business websites actually cost in India, what you get at each level, and how to spend smart.",
    category: "Website Development",
    date: "2026-07-12",
    readTime: "7 min read",
    sections: [
      {
        paragraphs: [
          "Ask five vendors what a business website costs in India and you'll get five wildly different answers — from a ₹4,999 template to a ₹5,00,000 agency quote. Both extremes usually disappoint. Here's an honest breakdown of what you actually get at each price level, and where the smart money goes.",
        ],
      },
      {
        h2: "The three price bands",
        list: [
          "₹5,000–₹15,000: template sites — fast and cheap, but generic design, no SEO structure, and no conversion thinking. Fine for a placeholder, costly as a sales tool.",
          "₹25,000–₹80,000: professional custom builds — premium design, mobile-first, SEO foundation, lead capture. The sweet spot for most businesses.",
          "₹1.5L–₹5L+: agency builds — often the same output with account managers, overheads and months of meetings baked into the price.",
        ],
      },
      {
        h2: "What actually drives cost",
        paragraphs: [
          "Page count matters less than you think. What drives cost: custom design vs template, content writing, SEO structure, integrations (WhatsApp, booking, payments), and who's doing the work — a senior developer directly, or an agency's junior after three layers of markup.",
        ],
      },
      {
        h2: "The hidden cost nobody mentions",
        paragraphs: [
          "A cheap website that generates no enquiries isn't ₹10,000 saved — it's months of lost customers. The real comparison isn't build cost; it's cost per enquiry over a year. A ₹40,000 website bringing 20 enquiries a month is cheaper than a free website bringing none.",
        ],
      },
      {
        h2: "The affordable + growth model",
        paragraphs: [
          "The model that works best for most Indian businesses: an affordable one-time build covering design, development, SEO foundation and lead capture — then an optional monthly plan for content, SEO and improvements as revenue grows. You get premium output now without betting the quarter's budget on it.",
        ],
      },
      {
        h2: "How to evaluate any quote",
        paragraphs: [
          "Ask three questions: Will it load fast on a phone? Is SEO built in or extra? How does a visitor become an enquiry? A vendor who answers these clearly is worth more than one who shows you a pretty portfolio and a low number.",
        ],
      },
    ],
  },
  {
    slug: "what-makes-website-generate-leads",
    title: "What Makes a Website Generate Leads?",
    excerpt: "Some websites generate enquiries every week; most generate none. The five structural differences between a brochure and a lead-generation website.",
    category: "Lead Generation",
    date: "2026-07-14",
    readTime: "6 min read",
    sections: [
      {
        paragraphs: [
          "Two businesses in the same city, same industry, similar traffic — one website generates ten enquiries a week, the other generates silence. The difference is never luck. Lead-generating websites share five structural traits that brochures lack.",
        ],
      },
      {
        h2: "1. It answers 'what's in it for me' in five seconds",
        paragraphs: [
          "A visitor decides in seconds whether you're relevant. The headline must say what you do, for whom, and the outcome — not 'Welcome to our website' or a slogan. Clarity converts; cleverness doesn't.",
        ],
      },
      {
        h2: "2. Every page has one job",
        paragraphs: [
          "A lead-generation website gives each page a single audience and a single action. A service page sells that service and asks for the enquiry. A location page wins that area's search and asks for the enquiry. Pages trying to do everything convert nothing.",
        ],
      },
      {
        h2: "3. Proof sits at every decision point",
        paragraphs: [
          "Reviews, case studies, client names and real photos — placed exactly where visitors decide whether to trust you, not exiled to a testimonials page. Proof near a CTA can double its conversion rate.",
        ],
      },
      {
        h2: "4. Contact is effortless",
        paragraphs: [
          "Every field you add to a form cuts submissions. The highest-converting setup in India: a short form plus a WhatsApp button with a prefilled message, so the enquiry lands in your pocket instantly. Speed of response wins deals — your website should make responding fast easy.",
        ],
      },
      {
        h2: "5. It's fast and mobile-perfect",
        list: [
          "Loads in under 3 seconds on 4G — slow sites lose half their visitors",
          "Thumb-friendly buttons and readable text without zooming",
          "Click-to-call and WhatsApp that actually work on mobile",
          "No popups blocking content before value is shown",
        ],
      },
      {
        h2: "The test",
        paragraphs: [
          "Open your website on your phone right now. Time how long until you understand the offer, find proof, and could send an enquiry. If any step takes effort, that's where your leads are leaking.",
        ],
      },
    ],
  },
  {
    slug: "website-redesign-vs-new-website",
    title: "Website Redesign vs Building a New Website: Which Is Better?",
    excerpt: "Your website isn't working — but do you redesign it or start fresh? A practical decision framework based on what you already have.",
    category: "Website Redesign",
    date: "2026-07-17",
    readTime: "6 min read",
    sections: [
      {
        paragraphs: [
          "When a website stops bringing business, owners face a fork: redesign what exists or build new. The right answer depends on what your current site still holds — because some of it is worth keeping, and some of it is quietly poisoning the rest.",
        ],
      },
      {
        h2: "What your old website might be worth",
        paragraphs: [
          "Three assets survive a rebuild if handled properly: your domain's age and authority with Google, any pages already ranking or getting traffic, and content customers still find useful. A proper redesign preserves all three through careful URL mapping and redirects. Abandoning them means starting SEO from zero.",
        ],
      },
      {
        h2: "Choose redesign when",
        list: [
          "The site has traffic or rankings worth protecting",
          "Content is decent but design, speed or mobile experience fails",
          "The structure is fine but nothing converts visitors",
          "Your domain has history and backlinks you don't want to lose",
        ],
      },
      {
        h2: "Choose a fresh build when",
        paragraphs: [
          "Start new when the site has no meaningful traffic, the domain carries no authority, the platform is unfixable, or the business has fundamentally changed what it sells. There's nothing to preserve — and a clean build is faster than surgery on a broken foundation.",
        ],
      },
      {
        h2: "The hybrid most businesses actually need",
        paragraphs: [
          "In practice, most redesigns are hybrids: same domain, preserved rankings, rewritten content, rebuilt design and structure. Your current site stays live while the new one is built, then switches over with redirects — no downtime, no lost Google equity.",
        ],
      },
      {
        h2: "The deciding question",
        paragraphs: [
          "Check Google Search Console (or ask your developer to): does your current site get impressions or clicks for anything? If yes, redesign carefully. If it's invisible anyway, you have nothing to lose — build the website you actually need.",
        ],
      },
    ],
  },
  {
    slug: "how-to-choose-website-development-company",
    title: "How to Choose a Website Development Company",
    excerpt: "Portfolios all look good. Prices vary 10x. Here's how to actually evaluate a website development company before you sign anything.",
    category: "Website Development",
    date: "2026-07-19",
    readTime: "6 min read",
    sections: [
      {
        paragraphs: [
          "Every website development company's portfolio looks impressive. Every proposal promises the world. And yet most businesses end up with websites that don't work. Choosing well comes down to asking questions that reveal how the company thinks — not just what it shows.",
        ],
      },
      {
        h2: "Look for business thinking, not just design",
        paragraphs: [
          "Ask: 'How will this website bring me enquiries?' A design-first answer talks about aesthetics. A business-first answer talks about your customers, what they search, and how pages convert them. The second answer builds you an asset; the first builds you a poster.",
        ],
      },
      {
        h2: "Verify the portfolio is real",
        list: [
          "Ask for live URLs, not screenshots — and actually open them on your phone",
          "Check load speed on mobile data, not office wifi",
          "Look for variety: different industries, different structures",
          "Ask what happened after launch — did enquiries or traffic improve?",
        ],
      },
      {
        h2: "Understand what's included",
        paragraphs: [
          "The cheapest quote usually excludes what matters most: content writing, SEO foundation, mobile optimisation, revisions, post-launch support. Compare what's included, not the headline number. A ₹15,000 site plus ₹30,000 of 'extras' costs more than an honest ₹40,000 build.",
        ],
      },
      {
        h2: "Check who you'll actually talk to",
        paragraphs: [
          "Agencies interpose account managers between you and the person building your site. Requirements get lost, changes take weeks. Direct access to the developer means faster work and fewer misunderstandings — ask upfront who you'll communicate with day to day.",
        ],
      },
      {
        h2: "The ownership test",
        paragraphs: [
          "Before signing, confirm: you own the domain, the website files, and the content. Some companies hold sites hostage with proprietary platforms or renewal leverage. A trustworthy company makes leaving easy — because they expect you to stay by choice.",
        ],
      },
    ],
  },
  {
    slug: "b2b-company-more-leads-from-website",
    title: "How Can a B2B Company Get More Leads From Its Website?",
    excerpt: "B2B websites fail at lead generation in predictable ways. Seven fixes that turn a corporate brochure into a qualified enquiry pipeline.",
    category: "B2B Growth",
    date: "2026-07-22",
    readTime: "7 min read",
    sections: [
      {
        paragraphs: [
          "Most B2B websites are digital brochures: company history, vague capability claims, a contact form. Meanwhile, your buyers — procurement heads, founders, managers — are researching suppliers online right now and shortlisting whoever answers their questions best. Here's how to become the answer.",
        ],
      },
      {
        h2: "1. Build pages for capabilities, not departments",
        paragraphs: [
          "Buyers search for what they need: 'precision CNC machining', 'bulk corrugated packaging', 'warehouse logistics NCR'. One page per capability, each ranking for its own search, beats an 'Our Services' list every time.",
        ],
      },
      {
        h2: "2. Prove scale before claiming it",
        paragraphs: [
          "'Trusted by leading companies' convinces nobody. Facility photos, production capacity, certifications, named client industries and case studies with real outcomes do. B2B buyers de-risk before they enquire — give them the evidence.",
        ],
      },
      {
        h2: "3. Qualify leads in the form",
        paragraphs: [
          "A B2B enquiry form should capture business type, requirement and approximate quantity or budget band. You'll know in seconds which enquiries deserve your fastest response — and serious buyers appreciate being asked serious questions.",
        ],
      },
      {
        h2: "4. Answer on WhatsApp, fast",
        paragraphs: [
          "B2B buyers message three suppliers and usually go with the first useful reply. WhatsApp integration puts enquiries in your pocket instantly. Response speed is a conversion feature most competitors haven't figured out.",
        ],
      },
      {
        h2: "5. Publish for the research phase",
        list: [
          "Pricing guidance and cost factors — the #1 thing buyers search",
          "How to evaluate a supplier in your industry",
          "Process explainers that demonstrate expertise",
          "Case studies showing problems solved, not just work done",
        ],
      },
      {
        h2: "6. Make the shortlist scan survivable",
        paragraphs: [
          "Buyers open five tabs and close four within seconds. Your homepage must instantly say what you make or do, for whom, and at what scale. If a stranger can't summarise your business after ten seconds, neither can a buyer.",
        ],
      },
      {
        h2: "7. Track enquiries like revenue",
        paragraphs: [
          "Tag every form submission and WhatsApp click. Within a month you'll know which pages produce buyers — and where to invest next. B2B lead generation compounds when you can see it.",
        ],
      },
    ],
  },
  {
    slug: "what-should-small-business-website-include",
    title: "What Should a Small Business Website Include?",
    excerpt: "The complete checklist for a small business website — the essentials that bring enquiries, and the expensive extras you can skip.",
    category: "Small Business Websites",
    date: "2026-07-26",
    readTime: "6 min read",
    sections: [
      {
        paragraphs: [
          "Small business owners get sold a lot of website they don't need. The truth: a small business website that brings enquiries needs surprisingly few things — but each one done properly. Here's the complete checklist, including what to skip.",
        ],
      },
      {
        h2: "The essentials",
        list: [
          "A homepage that says what you do, where, and for whom — in one glance",
          "One page per service, written for how customers search",
          "Reviews and real photos of your work — trust is everything for small businesses",
          "WhatsApp and click-to-call on every page",
          "Your exact location, hours and a Google Maps embed",
          "Fast loading on mobile — most of your customers are on phones",
        ],
      },
      {
        h2: "The SEO basics that come built-in (or should)",
        paragraphs: [
          "Unique page titles and descriptions, correct heading structure, your city and area named in text, a sitemap submitted to Google, and a connected Google Business Profile. These cost nothing extra in a good build — and decide whether anyone finds you.",
        ],
      },
      {
        h2: "What you can safely skip",
        paragraphs: [
          "Fancy animations, video backgrounds, live chat bots, member logins, blogs you won't maintain, and ten pages of company history. None of these bring a local customer through your door. Add them later if a real need appears — not because a package included them.",
        ],
      },
      {
        h2: "The one-page question",
        paragraphs: [
          "Can a single-page website work? For a brand-new business testing an idea, yes — temporarily. But each service and each area you serve deserves its own page to rank on Google. Plan the structure so pages can be added without rebuilding.",
        ],
      },
      {
        h2: "The affordability angle",
        paragraphs: [
          "Everything on this essentials list is achievable at a genuinely affordable cost — the expensive part of agency quotes is overhead, not quality. Get a custom plan based on exactly these essentials and you'll know what a fair price looks like.",
        ],
      },
    ],
  },
];
